import moment from "moment";
import { path } from "ramda";
import React, { useState, useEffect } from "react";
import { Alert, Card, Table } from "react-bootstrap";
import { useIntl, FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import genericTranslations from "../../../../../utils/genericTranslations.js";
import {
  isLoadingMalfunctions,
  selectMalfunctions,
} from "../../../selectors.js";
import { fetchPublicOpenMalfunctions } from "../../../thunks.js";

const styles = {
  container: {
    padding: "40px",
  },
  main: {
    backgroundColor: "#FAFAFA",
    padding: "30px",
  },
  fold: {
    display: "block",
    flexDirection: "column",
    cursor: "pointer",
  },
  description: {
    fontSize: 18,
  },
  long: {
    padding: "12px",
    fontSize: 18,
  },
  row: {
    height: "60px",
  },
  date: {
    fontSize: 18,
    width: "180px",
  },
  keyWord: {
    fontWeight: "bold",
    fontSize: 20,
  },
};

const messages = {
  vehicle: {
    id: "modules.Malfunctions.components.MalfunctionsList.vehicleWithNumber",
    defaultMessage: "Vehicle-number: {message}",
  },
  noVehicle: {
    id: "modules.Malfunctions.components.MalfunctionsList.noVehicle",
    defaultMessage:
      "Vehicle-number {message} doesn't exist or has no pending workorders",
  },
  workOrderCode: {
    id: "modules.Malfunctions.components.MalfunctionsList.workOrderCode",
    defaultMessage: "Workorder-code:",
  },
  description: {
    id: "modules.Malfunctions.components.CategoriesList.description",
    defaultMessage: "Description",
  },
  extendedDescription: {
    id: "modules.Malfunctions.components.MalfunctionsList.extendedDescription",
    defaultMessage: "Extended description:",
  },
  workOrderDate: {
    id: "modules.Malfunctions.components.MalfunctionsList.workOrderDate",
    defaultMessage: "Workorder-date",
  },
  workOrderStatus: {
    id: "modules.Malfunctions.components.MalfunctionsList.workOrderStatus",
    defaultMessage: "Status:",
  },
};

const OpenMalfunctionsList = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { identifier } = useParams();
  const { pathname } = useLocation();

  const [error, setError] = useState();
  const [showExtraInfo, setShowExtraInfo] = useState(false);
  const [workCode, setWorkCode] = useState();

  const malfunctions = useSelector(selectMalfunctions);
  const isLoading = useSelector(isLoadingMalfunctions);

  const internal = pathname.startsWith("/malfunctions/open-internal/");
  const rows = path(["rows"], malfunctions);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(
          fetchPublicOpenMalfunctions({ id: identifier, internal }),
        );
      } catch (err) {
        setError("Error fetching the open malfunctions: " + err.message);
      }
    };

    fetchData();

    const intervalId = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [dispatch, identifier]);

  const onClickHandler = (row) => {
    setShowExtraInfo((prev) => !prev);
    setWorkCode(row.workOrderCode);
  };

  const mapMalfunctionToTableRow = (row) => {
    moment.locale("nl");
    return (
      <tr
        style={styles.row}
        key={`malfunction-${row.workOrderCode}`}
        onClick={() => onClickHandler(row)}
      >
        <td style={styles.fold}>
          <p style={styles.description}>{row.failureDescription}</p>
          {showExtraInfo && workCode === row.workOrderCode && (
            <div style={styles.fold}>
              <div style={styles.long}>
                <p style={styles.keyWord}>
                  <FormattedMessage {...messages.extendedDescription} />
                </p>
                <p>{row.workOrderDescription}</p>
              </div>
              <div style={styles.long}>
                <p style={styles.keyWord}>
                  <FormattedMessage {...messages.workOrderCode} />
                </p>
                <p>{row.workOrderCode}</p>
              </div>
              <div style={styles.long}>
                <p style={styles.keyWord}>
                  <FormattedMessage {...messages.workOrderStatus} />
                </p>
                <p>{row.statusDescription}</p>
              </div>
            </div>
          )}
        </td>
        <td style={styles.date}>
          {moment(row.createdAt).format("DD MMM YYYY")}
        </td>
      </tr>
    );
  };

  const dismissError = () => setError(null);

  return (
    <div style={styles.container}>
      {error && (
        <Alert variant="danger" onClose={dismissError} dismissible>
          <Alert.Heading>
            {intl.formatMessage(genericTranslations.error)}
          </Alert.Heading>
          <p>{error.toString()}</p>
        </Alert>
      )}

      {isLoading && (
        <span className="align-middle">
          {intl.formatMessage(genericTranslations.loadingInProgress)}
        </span>
      )}

      {!isLoading &&
        (!malfunctions || malfunctions.length === 0) &&
        intl.formatMessage(genericTranslations.nothingFound)}

      {!isLoading && rows && rows.length > 0 && (
        <Card>
          <Card.Header>
            <FormattedMessage
              {...messages.vehicle}
              values={{ message: identifier }}
            />
          </Card.Header>
          <Card.Body>
            <Table responsive>
              <thead>
                <tr style={styles.row}>
                  <td style={styles.keyWord}>
                    <FormattedMessage {...messages.description} />
                  </td>
                  <td style={styles.keyWord}>
                    <FormattedMessage {...messages.workOrderDate} />
                  </td>
                </tr>
              </thead>
              <tbody>{rows.map(mapMalfunctionToTableRow)}</tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default OpenMalfunctionsList;
